import type { ReactNode } from 'react';

export type LabelTone = 'good' | 'power' | 'warn' | 'info' | 'accent' | 'mute';

export interface LabelPillProps {
  children: ReactNode;
  tone?: LabelTone;
  size?: 'sm' | 'md';
  className?: string;
  title?: string;
}

/**
 * Bordered pill where border and text share the tone colour — the core
 * building block of the Ferrari-Luce instrument-panel aesthetic. Every
 * status badge, card header, stat label, and delta indicator in the
 * dashboard system funnels through this one component.
 *
 * Tone maps to a semantic CSS custom property from `@evi/skins`; switching
 * skins recolours every pill in the app without any consumer changes.
 */
const TONE_VARS: Record<LabelTone, string> = {
  good: 'var(--evi-good)',
  power: 'var(--evi-power)',
  warn: 'var(--evi-warn)',
  info: 'var(--evi-info)',
  accent: 'var(--evi-accent)',
  mute: 'var(--evi-mute)',
};

export function LabelPill({
  children,
  tone = 'mute',
  size = 'sm',
  className,
  title,
}: LabelPillProps) {
  const color = TONE_VARS[tone];
  const padding = size === 'md' ? '3px 8px' : '2px 6px';
  const fontSize = size === 'md' ? '10px' : '9px';

  return (
    <span
      className={className}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding,
        border: `1px solid ${color}`,
        borderRadius: '3px',
        color,
        fontSize,
        fontWeight: 500,
        letterSpacing: '0.5px',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        textTransform: 'uppercase',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </span>
  );
}
